// src/components/admin/AdminSupportPage.jsx - COMPLETE
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  Chip,
  Paper,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Avatar,
  Divider,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  useMediaQuery,
  useTheme,
  Stack,
  List,
  ListItem,
} from '@mui/material';
import {
  Add,
  Email,
  Chat,
  Help,
  ConfirmationNumber,
  CheckCircle,
  Schedule,
  Close,
  Refresh,
  Reply,
  Visibility,
  Delete,
  Edit,
  Search,
  Download,
  Send,
  Warning,
  Done,
  Storefront,
  AccessTime,
  Phone,
  Clear,
  Timeline,
  HourglassEmpty,
  Pending,
  Person,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Shared components and constants
import { supportApi } from '../../api/supportApi';
import TicketList from '../Support/TicketList';
import StatusBadge from '../Support/StatusBadge';
import StatusFlow from '../Support/StatusFlow';
import StatusUpdateDialog from '../Support/StatusUpdateDialog';
import { SUPPORT_STATUSES, SUPPORT_CATEGORIES, PRIORITY_LEVELS, getCategoryLabel, getCategoryIcon } from '../../constants/supportConstants';

// ============================================================
// HELPER FUNCTIONS
// ============================================================
const getStatusConfig = (status) => {
  const found = SUPPORT_STATUSES.find(s => s.value === status);
  return found || SUPPORT_STATUSES[0];
};

const getStatusBgColor = (status) => {
  const config = getStatusConfig(status);
  const colorMap = {
    info: 'info',
    warning: 'warning',
    secondary: 'secondary',
    success: 'success',
    default: 'default',
  };
  return colorMap[config.color] || 'default';
};

const getPriorityBgColor = (priority) => {
  const config = PRIORITY_LEVELS.find(p => p.value === priority);
  const colorMap = {
    info: 'info',
    warning: 'warning',
    error: 'error',
  };
  return colorMap[config.color] || 'default';
};

const getStatusLabel = (status) => {
  const config = getStatusConfig(status);
  return config.label;
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const AdminSupportPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    waitingSeller: 0,
    waitingSupport: 0,
    resolved: 0,
    closed: 0,
    byPriority: { low: 0, medium: 0, high: 0, urgent: 0 },
  });
  const [faqs, setFaqs] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 5 : 10);
  const [totalTickets, setTotalTickets] = useState(0);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    category: 'all',
    search: '',
  });
  const [openTicketDetail, setOpenTicketDetail] = useState(false);
  const [openReplyDialog, setOpenReplyDialog] = useState(false);
  const [openFaqDialog, setOpenFaqDialog] = useState(false);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedFaq, setSelectedFaq] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [internalNote, setInternalNote] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: '',
    category: 'general',
    audience: 'all',
    order: 0,
    isActive: true,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ============================================================
  // FETCH DATA
  // ============================================================

  const fetchTickets = async () => {
    try {
      const response = await supportApi.getAllTickets({
        page: page + 1,
        limit: rowsPerPage,
        status: filters.status !== 'all' ? filters.status : undefined,
        priority: filters.priority !== 'all' ? filters.priority : undefined,
        category: filters.category !== 'all' ? filters.category : undefined,
        search: filters.search || undefined,
      });

      const ticketsData = response.tickets || [];
      const total = response.total || 0;

      setTickets(ticketsData);
      setTotalTickets(total);

      const calculatedStats = calculateStatsFromTickets(ticketsData);
      setStats(calculatedStats);

      return ticketsData;
    } catch (error) {
      console.error('Error fetching tickets:', error);
      showSnackbar('Failed to fetch tickets', 'error');
      return [];
    }
  };

  const calculateStatsFromTickets = (ticketList) => {
    const stats = {
      total: ticketList.length,
      open: 0,
      inProgress: 0,
      waitingSeller: 0,
      waitingSupport: 0,
      resolved: 0,
      closed: 0,
      byPriority: { low: 0, medium: 0, high: 0, urgent: 0 },
    };

    ticketList.forEach((ticket) => {
      switch (ticket.status) {
        case 'open':
          stats.open++;
          break;
        case 'in-progress':
          stats.inProgress++;
          break;
        case 'waiting-for-seller':
          stats.waitingSeller++;
          break;
        case 'waiting-for-support':
          stats.waitingSupport++;
          break;
        case 'resolved':
          stats.resolved++;
          break;
        case 'closed':
          stats.closed++;
          break;
        default:
          break;
      }

      if (ticket.priority && stats.byPriority[ticket.priority] !== undefined) {
        stats.byPriority[ticket.priority]++;
      }
    });

    return stats;
  };

  const fetchFaqs = async () => {
    try {
      const response = await supportApi.getFaqs({ page: 1, limit: 100 });
      const faqsData = response.faqs || [];
      setFaqs(faqsData);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      showSnackbar('Failed to fetch FAQs', 'error');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTickets(), fetchFaqs()]);
      setLoading(false);
    };
    loadData();
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    setRowsPerPage(isMobile ? 5 : 10);
  }, [isMobile]);

  // ============================================================
  // HANDLERS
  // ============================================================

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleReplyTicket = async () => {
    if (!replyMessage.trim()) {
      showSnackbar('Reply message is required', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await supportApi.replyTicket(selectedTicket.id, {
        message: replyMessage,
        internalNote,
      });
      showSnackbar('Reply sent successfully');
      setOpenReplyDialog(false);
      setReplyMessage('');
      setInternalNote(false);
      await fetchTickets();
    } catch (error) {
      console.error('Error sending reply:', error);
      showSnackbar('Failed to send reply', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (ticketId, status, note) => {
    try {
      await supportApi.updateTicketStatus(ticketId, status);
      showSnackbar(`Ticket status updated to ${getStatusLabel(status)}`);
      await fetchTickets();
      if (selectedTicket && selectedTicket.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, status });
      }
    } catch (error) {
      console.error('Error updating ticket status:', error);
      showSnackbar('Failed to update ticket status', 'error');
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this ticket?')) return;

    try {
      await supportApi.deleteTicket(ticketId);
      showSnackbar('Ticket deleted successfully');
      await fetchTickets();
      if (selectedTicket && selectedTicket.id === ticketId) {
        setOpenTicketDetail(false);
        setSelectedTicket(null);
      }
    } catch (error) {
      console.error('Error deleting ticket:', error);
      showSnackbar('Failed to delete ticket', 'error');
    }
  };

  const handleCreateFaq = async () => {
    if (!faqForm.question || !faqForm.answer) {
      showSnackbar('Question and answer are required', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await supportApi.createFaq(faqForm);
      showSnackbar('FAQ created successfully');
      setOpenFaqDialog(false);
      setFaqForm({
        question: '',
        answer: '',
        category: 'general',
        audience: 'all',
        order: 0,
        isActive: true,
      });
      await fetchFaqs();
    } catch (error) {
      console.error('Error creating FAQ:', error);
      showSnackbar('Failed to create FAQ', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateFaq = async () => {
    if (!faqForm.question || !faqForm.answer) {
      showSnackbar('Question and answer are required', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await supportApi.updateFaq(selectedFaq.id, faqForm);
      showSnackbar('FAQ updated successfully');
      setOpenFaqDialog(false);
      setSelectedFaq(null);
      setFaqForm({
        question: '',
        answer: '',
        category: 'general',
        audience: 'all',
        order: 0,
        isActive: true,
      });
      await fetchFaqs();
    } catch (error) {
      console.error('Error updating FAQ:', error);
      showSnackbar('Failed to update FAQ', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteFaq = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      await supportApi.deleteFaq(faqId);
      showSnackbar('FAQ deleted successfully');
      await fetchFaqs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      showSnackbar('Failed to delete FAQ', 'error');
    }
  };

  const refreshAll = async () => {
    setLoading(true);
    await Promise.all([fetchTickets(), fetchFaqs()]);
    setLoading(false);
    showSnackbar('Data refreshed successfully');
  };

  const downloadReport = () => {
    const headers = [
      'ID',
      'Ticket #',
      'Vendor',
      'Subject',
      'Status',
      'Priority',
      'Category',
      'Created',
    ];
    const rows = tickets.map((t) => [
      t.id,
      t.ticketNumber || 'N/A',
      t.Vendor?.storeName || 'N/A',
      t.subject || 'No subject',
      t.status || 'Unknown',
      t.priority || 'N/A',
      t.category || 'General',
      formatDate(t.createdAt),
    ]);

    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tickets-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setFilters({ status: 'all', priority: 'all', category: 'all', search: '' });
    setPage(0);
  };

  // ============================================================
  // RENDER COMPONENTS
  // ============================================================

  const renderStatCard = (label, value, color, icon) => (
    <Grid item xs={6} sm={4} md={2}>
      <Card
        sx={{
          height: '100%',
          borderLeft: `4px solid ${color}`,
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[4],
          },
        }}
      >
        <CardContent sx={{ textAlign: 'center', py: { xs: 1.5, sm: 2 } }}>
          <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
            {icon && <Box sx={{ color }}>{icon}</Box>}
            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" sx={{ color }}>
              {value || 0}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {label}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  );

  const renderFaqTable = () => {
    if (faqs.length === 0) {
      return (
        <Paper sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center', borderRadius: 2 }}>
          <Help sx={{ fontSize: { xs: 48, sm: 60 }, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No FAQs Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create your first FAQ to help vendors and customers.
          </Typography>
        </Paper>
      );
    }

    return (
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.50' }}>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  #
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  Question
                </Typography>
              </TableCell>
              {!isMobile && (
                <>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Category
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Audience
                    </Typography>
                  </TableCell>
                </>
              )}
              <TableCell>
                <Typography variant="subtitle2" fontWeight="bold">
                  Status
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="subtitle2" fontWeight="bold">
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {faqs.map((faq, index) => (
              <TableRow key={faq.id} hover>
                <TableCell>{index + 1}</TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: isMobile ? 120 : 300 }}>
                    {faq.question}
                  </Typography>
                </TableCell>
                {!isMobile && (
                  <>
                    <TableCell>
                      <Chip
                        label={getCategoryLabel(faq.category)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={faq.audience || 'All'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                  </>
                )}
                <TableCell>
                  <Chip
                    label={faq.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={faq.isActive ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedFaq(faq);
                        setFaqForm(faq);
                        setOpenFaqDialog(true);
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteFaq(faq.id)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // ============================================================
  // MAIN RENDER
  // ============================================================

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 }, pb: { xs: 10, sm: 12 } }}>
      {/* Header */}
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">
            Support Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage all support tickets and customer inquiries
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap" width={{ xs: '100%', sm: 'auto' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={refreshAll}
            size={isMobile ? 'small' : 'medium'}
            sx={{ flex: isMobile ? 1 : 'none' }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={downloadReport}
            size={isMobile ? 'small' : 'medium'}
            sx={{ flex: isMobile ? 1 : 'none' }}
          >
            Export
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: 4 }}>
        {renderStatCard('Total', stats.total, theme.palette.primary.main, <ConfirmationNumber fontSize="small" />)}
        {renderStatCard('Open', stats.open, '#1976d2', <Email fontSize="small" />)}
        {renderStatCard('In Progress', stats.inProgress, '#ed6c02', <Schedule fontSize="small" />)}
        {renderStatCard('Waiting Seller', stats.waitingSeller, '#ed6c02', <HourglassEmpty fontSize="small" />)}
        {renderStatCard('Waiting Support', stats.waitingSupport, '#9c27b0', <Pending fontSize="small" />)}
        {renderStatCard('Resolved', stats.resolved, '#2e7d32', <CheckCircle fontSize="small" />)}
        {renderStatCard('Closed', stats.closed, '#9e9e9e', <Close fontSize="small" />)}
        {renderStatCard('Urgent', stats.byPriority?.urgent || 0, '#d32f2f', <Warning fontSize="small" />)}
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={selectedTab}
          onChange={(e, v) => setSelectedTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? 'fullWidth' : 'standard'}
          centered={!isMobile}
          sx={{
            '& .MuiTab-root': {
              minHeight: { xs: 48, sm: 56 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
            },
          }}
        >
          <Tab icon={<ConfirmationNumber />} label={isMobile ? 'Tickets' : 'Tickets'} />
          <Tab icon={<Help />} label={isMobile ? 'FAQs' : 'FAQs'} />
          <Tab icon={<Chat />} label={isMobile ? 'Chat' : 'Live Chat'} />
        </Tabs>
      </Paper>

      {/* ============================================================ */}
      {/* TAB 0: TICKETS */}
      {/* ============================================================ */}
      {selectedTab === 0 && (
        <Box>
          {/* Filters */}
          <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 2 }}>
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              gap={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="Search tickets..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: filters.search && (
                    <IconButton
                      size="small"
                      onClick={() => setFilters({ ...filters, search: '' })}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  ),
                }}
                sx={{ flex: 1 }}
              />

              <Box display="flex" gap={1} flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 120 } }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    {SUPPORT_STATUSES.map((s) => (
                      <MenuItem key={s.value} value={s.value}>
                        {s.icon} {s.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 120 } }}>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={filters.priority}
                    label="Priority"
                    onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                  >
                    <MenuItem value="all">All Priority</MenuItem>
                    {PRIORITY_LEVELS.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 120 } }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={filters.category}
                    label="Category"
                    onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    {SUPPORT_CATEGORIES.map((c) => (
                      <MenuItem key={c.value} value={c.value}>
                        {c.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {(filters.status !== 'all' ||
                  filters.priority !== 'all' ||
                  filters.category !== 'all' ||
                  filters.search) && (
                  <Button size="small" onClick={clearFilters} startIcon={<Clear />}>
                    Clear
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>

          {/* Tickets Table/List */}
          <TicketList
			  tickets={tickets}
			  onViewTicket={(ticket) => {
				setSelectedTicket(ticket);
				setOpenTicketDetail(true);
			  }}
			  onStatusUpdate={(ticket) => {
				setSelectedTicket(ticket);
				setOpenStatusDialog(true);
			  }}
			  onReplyTicket={(ticket) => {
				setSelectedTicket(ticket);
				setOpenReplyDialog(true);
			  }}
			  onDeleteTicket={handleDeleteTicket}
			  loading={loading}
			  isVendor={false}
			  showVendor={true}
			/>

          {/* Pagination */}
          <Paper sx={{ mt: 2, borderRadius: 2 }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalTickets}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        </Box>
      )}

      {/* ============================================================ */}
      {/* TAB 1: FAQS */}
      {/* ============================================================ */}
      {selectedTab === 1 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Manage FAQs</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setSelectedFaq(null);
                setFaqForm({
                  question: '',
                  answer: '',
                  category: 'general',
                  audience: 'all',
                  order: 0,
                  isActive: true,
                });
                setOpenFaqDialog(true);
              }}
              size={isMobile ? 'small' : 'medium'}
            >
              Add FAQ
            </Button>
          </Box>

          {renderFaqTable()}
        </Box>
      )}

      {/* ============================================================ */}
      {/* TAB 2: LIVE CHAT */}
      {/* ============================================================ */}
      {selectedTab === 2 && (
        <Card sx={{ borderRadius: 2 }}>
          <CardContent sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
            <Chat sx={{ fontSize: { xs: 60, sm: 80 }, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Live Chat Dashboard
            </Typography>
            <Typography variant="body2" paragraph color="text.secondary">
              Monitor and respond to live chat sessions from vendors and customers.
            </Typography>
            <Box
              display="flex"
              justifyContent="center"
              gap={{ xs: 2, sm: 4 }}
              flexWrap="wrap"
              sx={{ mb: 3 }}
            >
              <Box textAlign="center">
                <Person sx={{ color: 'primary.main', fontSize: { xs: 28, sm: 35 } }} />
                <Typography variant="caption" display="block" color="text.secondary">
                  Active Agents
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  3
                </Typography>
              </Box>
              <Box textAlign="center">
                <Chat sx={{ color: 'warning.main', fontSize: { xs: 28, sm: 35 } }} />
                <Typography variant="caption" display="block" color="text.secondary">
                  Active Chats
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {stats.open || 0}
                </Typography>
              </Box>
              <Box textAlign="center">
                <AccessTime sx={{ color: 'success.main', fontSize: { xs: 28, sm: 35 } }} />
                <Typography variant="caption" display="block" color="text.secondary">
                  Avg Response
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  &lt; 1 min
                </Typography>
              </Box>
              <Box textAlign="center">
                <Phone sx={{ color: 'info.main', fontSize: { xs: 28, sm: 35 } }} />
                <Typography variant="caption" display="block" color="text.secondary">
                  Support
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  24/7
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              size="large"
              startIcon={<Chat />}
              onClick={() => window.open('/admin/messages', '_blank')}
              fullWidth={isMobile}
            >
              Open Chat Dashboard
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ============================================================ */}
      {/* DIALOG: TICKET DETAIL */}
      {/* ============================================================ */}
      <Dialog
        open={openTicketDetail}
        onClose={() => setOpenTicketDetail(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            gap={1}
          >
            <Box>
              <Typography variant="h6">Ticket Details</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedTicket?.ticketNumber || `#${selectedTicket?.id}`}
              </Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              {selectedTicket &&
                selectedTicket.status !== 'closed' &&
                selectedTicket.status !== 'resolved' && (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      startIcon={<Schedule />}
                      onClick={() => {
                        handleUpdateStatus(selectedTicket.id, 'in-progress');
                      }}
                    >
                      In Progress
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<Done />}
                      onClick={() => {
                        handleUpdateStatus(selectedTicket.id, 'resolved');
                      }}
                    >
                      Resolve
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<Close />}
                      onClick={() => {
                        handleUpdateStatus(selectedTicket.id, 'closed');
                      }}
                    >
                      Close
                    </Button>
                  </>
                )}
              <IconButton onClick={() => setOpenTicketDetail(false)}>
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTicket && (
            <>
              {/* Ticket Details */}
              <Box mb={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Subject
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedTicket.subject || 'No subject'}
                </Typography>
              </Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <StatusBadge status={selectedTicket.status} />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Priority
                  </Typography>
                  <Chip
                    label={selectedTicket.priority || 'N/A'}
                    color={getPriorityBgColor(selectedTicket.priority)}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Vendor
                  </Typography>
                  <Typography variant="body2">
                    {selectedTicket.Vendor?.storeName || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body2" textTransform="capitalize">
                    {getCategoryLabel(selectedTicket.category)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Message
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mt: 0.5 }}>
                    <Typography variant="body2">
                      {selectedTicket.message || 'No message'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Status Flow */}
              <StatusFlow
                currentStatus={selectedTicket.status}
                resolvedAt={selectedTicket.resolvedAt}
                closedAt={selectedTicket.closedAt}
              />

              {/* Replies */}
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                Replies ({selectedTicket.SupportReplies?.length || 0})
              </Typography>
              {selectedTicket.SupportReplies && selectedTicket.SupportReplies.length > 0 ? (
                <List>
                  {selectedTicket.SupportReplies.map((reply) => (
                    <Paper
                      key={reply.id}
                      sx={{
                        mb: 1,
                        p: 2,
                        bgcolor: reply.isAdmin ? 'grey.50' : 'background.paper',
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={1}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              bgcolor: reply.isAdmin ? 'primary.main' : 'secondary.main',
                            }}
                          >
                            {reply.isAdmin ? 'A' : 'V'}
                          </Avatar>
                          <Typography variant="subtitle2">
                            {reply.isAdmin
                              ? 'Support Team'
                              : selectedTicket.Vendor?.storeName || 'Vendor'}
                          </Typography>
                          {reply.internalNote && (
                            <Chip
                              label="Internal Note"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(reply.createdAt)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {reply.message}
                      </Typography>
                    </Paper>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" align="center" py={2}>
                  No replies yet
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setOpenTicketDetail(false)} fullWidth={isMobile}>
            Close
          </Button>
          {selectedTicket &&
            selectedTicket.status !== 'closed' &&
            selectedTicket.status !== 'resolved' && (
              <Button
                variant="contained"
                startIcon={<Reply />}
                onClick={() => {
                  setOpenTicketDetail(false);
                  setOpenReplyDialog(true);
                }}
                fullWidth={isMobile}
              >
                Reply
              </Button>
            )}
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* DIALOG: REPLY TO TICKET */}
      {/* ============================================================ */}
      <Dialog
        open={openReplyDialog}
        onClose={() => setOpenReplyDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Reply to Ticket</Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            {selectedTicket?.ticketNumber || `#${selectedTicket?.id}`}:{' '}
            {selectedTicket?.subject || 'No subject'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reply Message"
            value={replyMessage}
            onChange={(e) => setReplyMessage(e.target.value)}
            sx={{ mt: 2 }}
            required
            placeholder="Type your reply here..."
          />
          <FormControlLabel
            control={
              <Switch
                checked={internalNote}
                onChange={(e) => setInternalNote(e.target.checked)}
              />
            }
            label="Internal Note (only visible to admins)"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setOpenReplyDialog(false)} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleReplyTicket}
            disabled={submitting}
            fullWidth={isMobile}
          >
            {submitting ? <CircularProgress size={24} /> : 'Send Reply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* STATUS UPDATE DIALOG */}
      {/* ============================================================ */}
      <StatusUpdateDialog
        open={openStatusDialog}
        ticket={selectedTicket}
        onClose={() => setOpenStatusDialog(false)}
        onUpdate={handleUpdateStatus}
      />

      {/* ============================================================ */}
      {/* DIALOG: CREATE/EDIT FAQ */}
      {/* ============================================================ */}
      <Dialog
        open={openFaqDialog}
        onClose={() => setOpenFaqDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedFaq ? 'Edit FAQ' : 'Create New FAQ'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Question"
            value={faqForm.question}
            onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
            sx={{ mt: 2 }}
            required
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Answer"
            value={faqForm.answer}
            onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
            sx={{ mt: 2 }}
            required
          />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={faqForm.category}
                  label="Category"
                  onChange={(e) => setFaqForm({ ...faqForm, category: e.target.value })}
                >
                  {SUPPORT_CATEGORIES.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Audience</InputLabel>
                <Select
                  value={faqForm.audience}
                  label="Audience"
                  onChange={(e) => setFaqForm({ ...faqForm, audience: e.target.value })}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="vendor">Vendor</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="buyer">Buyer</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Order"
                value={faqForm.order}
                onChange={(e) =>
                  setFaqForm({ ...faqForm, order: parseInt(e.target.value) || 0 })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={faqForm.isActive}
                    onChange={(e) => setFaqForm({ ...faqForm, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button onClick={() => setOpenFaqDialog(false)} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={selectedFaq ? handleUpdateFaq : handleCreateFaq}
            disabled={submitting}
            fullWidth={isMobile}
          >
            {submitting ? <CircularProgress size={24} /> : selectedFaq ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* SNACKBAR */}
      {/* ============================================================ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminSupportPage;